import { useState, type ReactNode } from 'react'
import { Box, Flex, Text, Input } from '@chakra-ui/react'
import { AnimatePresence, motion } from 'framer-motion'
import {
  LuSearch as Search,
  LuDownload as Download,
  LuChevronLeft as ChevronLeft,
  LuChevronRight as ChevronRight,
  LuX,
} from 'react-icons/lu'
import { DataTable, TableMessage, TableSkeleton, type DataTableProps } from './DataTable'
import { ACCENT, ACCENT_SOFT, EASE_OUT, FONT_MONO, SHADOW_MD } from './tokens'

const MotionBox = motion.create(Box)

export interface DataListProps<T> extends DataTableProps<T> {
  searchValue: string
  onSearchChange: (value: string) => void
  searchPlaceholder?: string
  onExport?: () => void
  exportLabel?: string
  /**
   * Exporta el listado completo, no sólo la página visible. Cuando se provee, el botón
   * de exportar pasa a decir "Página actual" para que quede claro qué baja cada uno.
   */
  onExportAll?: () => void | Promise<void>
  exportAllLabel?: string
  page: number
  totalPages: number
  onPageChange: (page: number) => void
  totalCount: number
  entityName?: string
  /**
   * Tamaño de página vigente. Tiene que ser el MISMO valor que usa quien pide los datos:
   * de acá salen los "Mostrando X - Y de Z", así que si se desincroniza, el contador miente.
   */
  pageSize?: number
  /** Cuando se provee, el footer ofrece cambiar el tamaño de página. */
  onPageSizeChange?: (size: number) => void
  pageSizeOptions?: readonly number[]
  /** @deprecated el acento sale del mineral del módulo (`--k-accent`); se ignora. */
  brandColor?: string
  toolbarExtra?: ReactNode
  /** Cuando es 'cards' y se provee renderCards, el cuerpo muestra tarjetas en vez de la tabla. */
  viewMode?: 'table' | 'cards'
  renderCards?: (items: T[]) => ReactNode
}

/** Páginas a mostrar: 1 … 4 5 [6] 7 8 … 20 */
function pageWindow(page: number, total: number): Array<number | '…'> {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
  const out: Array<number | '…'> = [1]
  const from = Math.max(2, page - 1)
  const to = Math.min(total - 1, page + 1)
  if (from > 2) out.push('…')
  for (let p = from; p <= to; p++) out.push(p)
  if (to < total - 1) out.push('…')
  out.push(total)
  return out
}

function GhostButton({ onClick, children, colors }: { onClick: () => void; children: ReactNode; colors: DataTableProps<unknown>['colors'] }) {
  const [done, setDone] = useState(false)
  return (
    <Box
      as="button" {...{ type: 'button' }}
      display="flex"
      alignItems="center"
      gap={1.5}
      px={3}
      h="36px"
      color={done ? ACCENT : colors.textMuted}
      fontSize="13px"
      fontWeight="600"
      cursor="pointer"
      borderRadius="10px"
      border="1px solid"
      borderColor={done ? ACCENT : 'transparent'}
      _hover={{ color: colors.text, bg: colors.bgHover }}
      transition="all 0.15s"
      onClick={() => {
        onClick()
        // Confirmación breve de que la descarga salió: el botón "late" en el color del módulo.
        setDone(true)
        setTimeout(() => setDone(false), 900)
      }}
    >
      <motion.span animate={done ? { y: [0, 4, 0] } : { y: 0 }} transition={{ duration: 0.4 }} style={{ display: 'flex' }}>
        <Download size={15} />
      </motion.span>
      {children}
    </Box>
  )
}

export function DataList<T>({
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Buscar...',
  onExport,
  exportLabel = 'Exportar',
  onExportAll,
  exportAllLabel = 'Toda la lista',
  page,
  totalPages,
  onPageChange,
  totalCount,
  entityName = 'registros',
  pageSize = 25,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
  toolbarExtra,
  viewMode = 'table',
  renderCards,
  ...tableProps
}: DataListProps<T>) {
  const { colors } = tableProps
  const [focused, setFocused] = useState(false)

  const startItem = totalCount === 0 ? 0 : (page - 1) * pageSize + 1
  const endItem = Math.min(page * pageSize, totalCount)

  const toolbar = (
    <Flex gap={3} alignItems="center" flexWrap="wrap" px={4} py={3} borderBottom="1px solid" borderColor={colors.border}>
      <Box position="relative" flex={1} minW="220px" maxW="440px">
        <Box position="absolute" left={3.5} top="50%" transform="translateY(-50%)" color={focused ? ACCENT : colors.textMuted} zIndex={1} transition="color .15s">
          <Search size={16} />
        </Box>
        <Input
          pl={10}
          pr={searchValue ? 9 : 3}
          h="40px"
          placeholder={searchPlaceholder}
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          bg={focused ? colors.bgPanel : colors.bgCard}
          border="1px solid"
          borderColor={focused ? ACCENT : colors.border}
          borderRadius="12px"
          fontSize="14px"
          color={colors.text}
          _placeholder={{ color: colors.textMuted }}
          _hover={{ borderColor: focused ? ACCENT : colors.textMuted }}
          _focus={{ outline: 'none', boxShadow: `0 0 0 4px ${ACCENT_SOFT}` }}
          transition="all .15s"
        />
        <AnimatePresence>
          {searchValue && (
            <motion.button
              type="button"
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.6 }}
              onClick={() => onSearchChange('')}
              aria-label="Limpiar búsqueda"
              style={{ position: 'absolute', right: 8, top: '50%', marginTop: -12, width: 24, height: 24, borderRadius: 8, border: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: ACCENT_SOFT, color: ACCENT, cursor: 'pointer', zIndex: 1 }}
            >
              <LuX size={13} />
            </motion.button>
          )}
        </AnimatePresence>
      </Box>

      {toolbarExtra}

      <Box flex={1} />

      {!tableProps.loading && (
        <Text fontFamily={FONT_MONO} fontSize="11.5px" color={colors.textMuted} whiteSpace="nowrap">
          <motion.span key={totalCount} initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} style={{ display: 'inline-block', color: colors.text, fontWeight: 600 }}>
            {totalCount.toLocaleString('es-PE')}
          </motion.span>{' '}{entityName}
        </Text>
      )}

      {onExport && (
        <GhostButton onClick={onExport} colors={colors}>
          {/* Con los dos botones presentes hay que decir qué baja cada uno. */}
          {onExportAll ? 'Página actual' : exportLabel}
        </GhostButton>
      )}
      {onExportAll && <GhostButton onClick={() => { void onExportAll() }} colors={colors}>{exportAllLabel}</GhostButton>}
    </Flex>
  )

  const navBtn = (disabled: boolean, onClick: () => void, children: ReactNode, label: string) => (
    <Box
      as="button" {...{ type: 'button', 'aria-label': label }}
      display="flex"
      alignItems="center"
      justifyContent="center"
      w="32px"
      h="32px"
      borderRadius="10px"
      color={colors.text}
      cursor={disabled ? 'default' : 'pointer'}
      opacity={disabled ? 0.3 : 1}
      _hover={disabled ? {} : { bg: colors.bgHover }}
      transition="all 0.15s"
      onClick={() => !disabled && onClick()}
    >
      {children}
    </Box>
  )

  const paginationFooter = totalCount > 0 ? (
    <Flex alignItems="center" justifyContent="space-between" px={4} py={3} borderTop="1px solid" borderColor={colors.border} bg={colors.bgCard} gap={3} flexWrap="wrap">
      <Flex alignItems="center" gap={2}>
        <Text fontSize="12.5px" color={colors.textMuted}>
          {startItem}–{endItem} de {totalCount.toLocaleString('es-PE')}
        </Text>
        {onPageSizeChange && (
          <Flex alignItems="center" gap={1} ml={2} p="3px" borderRadius="10px" bg={colors.bgHover}>
            {pageSizeOptions.map((n) => (
              <Box
                key={n}
                as="button" {...{ type: 'button' }}
                position="relative"
                px={2.5}
                h="24px"
                borderRadius="7px"
                fontSize="12px"
                fontWeight={600}
                color={n === pageSize ? colors.text : colors.textMuted}
                cursor="pointer"
                onClick={() => onPageSizeChange(n)}
              >
                {n === pageSize && (
                  <motion.span layoutId="k-pagesize" transition={{ type: 'spring', stiffness: 600, damping: 40 }}
                    style={{ position: 'absolute', inset: 0, borderRadius: 7, background: colors.bgPanel, boxShadow: '0 1px 2px rgba(74,52,30,0.08)' }} />
                )}
                <Text as="span" position="relative">{n}</Text>
              </Box>
            ))}
          </Flex>
        )}
      </Flex>

      <Flex alignItems="center" gap={1}>
        {navBtn(page <= 1, () => onPageChange(page - 1), <ChevronLeft size={16} />, 'Página anterior')}
        {pageWindow(page, Math.max(totalPages, 1)).map((p, i) =>
          p === '…' ? (
            <Text key={`gap-${i}`} px={1} color={colors.textMuted} fontSize="13px">…</Text>
          ) : (
            <Box
              key={p}
              as="button" {...{ type: 'button' }}
              position="relative"
              minW="32px"
              h="32px"
              px={2}
              borderRadius="10px"
              fontSize="13px"
              fontWeight={600}
              color={p === page ? '#fff' : colors.text}
              cursor={p === page ? 'default' : 'pointer'}
              _hover={p === page ? {} : { bg: colors.bgHover }}
              onClick={() => p !== page && onPageChange(p)}
            >
              {p === page && (
                <motion.span layoutId="k-page" transition={{ type: 'spring', stiffness: 500, damping: 38 }}
                  style={{ position: 'absolute', inset: 0, borderRadius: 10, background: ACCENT, boxShadow: `0 6px 14px -6px ${ACCENT}` }} />
              )}
              <Text as="span" position="relative">{p}</Text>
            </Box>
          ),
        )}
        {navBtn(page >= totalPages, () => onPageChange(page + 1), <ChevronRight size={16} />, 'Página siguiente')}
      </Flex>
    </Flex>
  ) : undefined

  return (
    <MotionBox
      bg={colors.bgPanel}
      border="1px solid"
      borderColor={colors.border}
      borderRadius="22px"
      overflow="hidden"
      boxShadow={SHADOW_MD}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.05, ease: EASE_OUT }}
    >
      {toolbar}

      {viewMode === 'cards' && renderCards ? (
        <Box px={4} py={4} bg={colors.bgCard}>
          {tableProps.loading ? (
            <TableSkeleton columns={4} colors={colors} rows={4} />
          ) : tableProps.error ? (
            <TableMessage kind="error" message={tableProps.error} colors={colors} />
          ) : tableProps.data.length === 0 ? (
            <TableMessage kind="empty" message={tableProps.emptyMessage ?? 'Sin registros'} colors={colors} />
          ) : (
            renderCards(tableProps.data)
          )}
        </Box>
      ) : (
        <DataTable<T> {...tableProps} totalCount={undefined} _noCard />
      )}

      {paginationFooter}
    </MotionBox>
  )
}
